import React from "react";
import { useTranslation } from "react-i18next";
import styled from "@emotion/styled";
import { keepPreviousData } from "@tanstack/react-query";
import { Box, Theme, Typography, useMediaQuery } from "@mui/material";
import { TransactionV2 } from "@rentablo/api/apiv1";
import { RangeSelectorControlProps, useTableSort } from "@hooks";
import { DebouncedSearch, TableSummary } from "@components";
import { AutoTable, LoadingPlaceHolder } from "@rentablo/components";
import { useInfiniteTransactionsV2 } from "@api/v1/hooks/useTransactions";
import { filterTransactionsByPurposeAndInvestmentName } from "@helpers/filters";
import { AccountTransactionsExportButton } from "../AccountTransactionsExportButton";
import { useAccountProtocolTableConfig } from "./useAccountProtocolTableConfig";
import { AccountProtocolMobileList } from "./AccountProtocolMobileList";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: ${({ theme }) => theme.spacing(1, 0, 3)};
  width: 100%;
`;

const Header = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: theme.spacing(3),

  [theme.breakpoints.down(750)]: {
    flexDirection: "column",
    alignItems: "flex-start",
  },
}));

interface AccountProtocolTableProps {
  summaryValue: number;
  accountId: number;
  currency?: string;
  rangeProps: RangeSelectorControlProps;
  filter: string;
  onFilterChange: (value: string) => void;
}

export const AccountProtocolTable = ({
  summaryValue,
  accountId,
  currency,
  rangeProps,
  filter,
  onFilterChange,
}: AccountProtocolTableProps) => {
  const isMoreSM = useMediaQuery((theme: Theme) => theme.breakpoints.up("sm"));

  const { t } = useTranslation();

  const { range } = rangeProps;

  const { sortField, sortDirection, sort } = useTableSort({
    field: "valueDate",
    direction: "DESC",
  });

  const {
    data: transactionsResponse,
    isFetching: isTableDataLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteTransactionsV2(
    {
      accountId: [accountId],
      minDate: range?.startDate?.toISOString(),
      maxDate: range?.endDate.toISOString(),
      order: [`${sortField} ${sortDirection?.toLowerCase()}`],
      include: ["bookings"],
      includeAdjustingEntries: true,
      displayCurrency: currency,
    },
    {
      enabled: Boolean(accountId),
      initialPageParam: { page: 0 },
      placeholderData: keepPreviousData,
      getNextPageParam: (lastPage) => {
        if (lastPage.paging?.pageCount === lastPage.paging?.page)
          return undefined;
        return (lastPage.paging?.page || 0) + 1;
      },
    }
  );

  const config = useAccountProtocolTableConfig({
    sortField,
    sortDirection,
    onSort: sort,
  });

  const transactions = (transactionsResponse?.pages || []).reduce(
    (acc, page) => {
      return [...acc, ...(page?.transactions || [])];
    },
    [] as TransactionV2[]
  );

  const filteredTransactions = filterTransactionsByPurposeAndInvestmentName(
    transactions,
    filter
  );

  if (!isMoreSM) {
    return (
      <Wrapper data-testid="protocol-table">
        <Typography variant="h6" component="h2">
          {t("singleAccountPage.protocolTable.title")}
        </Typography>
        <AccountProtocolMobileList
          accountId={accountId}
          currency={currency}
          transactions={filteredTransactions}
          totalCount={
            filter
              ? filteredTransactions.length
              : transactionsResponse?.pages[0]?.paging?.totalCount
          }
          rangeProps={rangeProps}
          filter={filter}
          onFilterChange={onFilterChange}
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={sort}
          isRefreshing={isTableDataLoading && !isFetchingNextPage}
          hasMore={Boolean(hasNextPage)}
          isLoadingMore={isFetchingNextPage}
          onLoadMore={() => {
            if (hasNextPage && !isFetchingNextPage) fetchNextPage();
          }}
          showEndOfList={(transactionsResponse?.pages.length ?? 0) > 1}
        />
      </Wrapper>
    );
  }

  return (
    <Wrapper data-testid="protocol-table">
      <Header>
        <Typography variant="h6" component="h2">
          {t("singleAccountPage.protocolTable.title")}
        </Typography>
        {isMoreSM && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
            gap={3}
            width="100%"
          >
            <DebouncedSearch
              id="account-transactions-search"
              value={filter}
              onDebouncedChange={onFilterChange}
              sx={{
                "& .MuiInputBase-root": {
                  height: "36px",
                },
              }}
            />
            <AccountTransactionsExportButton
              accountId={accountId}
              range={range}
              currency={currency}
              order={[`${sortField} ${sortDirection?.toLowerCase()}`]}
            />
          </Box>
        )}
      </Header>
      <TableSummary
        summaryValue={summaryValue}
        currencySymbol={currency}
        rangeProps={rangeProps}
        hideMobileRangeSelector
      />
      <Box>
        <AutoTable
          settings={config}
          data={filteredTransactions}
          additionalProps={{ currency }}
          isFetching={isTableDataLoading && !isFetchingNextPage}
          paging={{
            type: "infinite",
            hasMore: hasNextPage || false,
            next: () => {
              fetchNextPage();
            },
            loader: isFetchingNextPage ? <LoadingPlaceHolder /> : null,
          }}
          showNoData={!isTableDataLoading && filteredTransactions.length === 0}
          noDataFirstLine={t("singleAccountPage.protocolTable.noItems")}
        />
      </Box>
    </Wrapper>
  );
};
